/* eslint-disable no-var */
'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

export default function DriftWidget() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isDriftLoaded, setIsDriftLoaded] = useState(false);
  const [hasConsentedToFunctional, setHasConsentedToFunctional] = useState(false);

  // Function to check current consent state from dataLayer
  const checkCurrentConsentState = () => {
    if (typeof window === 'undefined' || !window.dataLayer) return false;
    
    // Find the most recent consent_updated event in dataLayer
    const consentEvents = window.dataLayer.filter(item => 
      item.event === 'consent_updated' && item.consent_state
    );
    
    if (consentEvents.length === 0) return false;
    
    // Get the most recent consent state
    const latestConsent = consentEvents[consentEvents.length - 1];
    return latestConsent.consent_state?.functional === 'granted';
  };

  // Function to initialize Drift
  const initializeDrift = () => {
    if (typeof window === 'undefined' || window.drift || isDriftLoaded) return;

    // Drift widget code
    !(function () {
      var t = (window.driftt = window.drift = window.driftt || []);
      if (!t.init) {
        if (t.invoked) return void (window.console && console.error && console.error('Drift snippet included twice.'));
        (t.invoked = !0),
          (t.methods = ['identify', 'config', 'track', 'reset', 'debug', 'show', 'ping', 'page', 'hide', 'off', 'on']),
          (t.factory = function (e) {
            return function () {
              var n = Array.prototype.slice.call(arguments);
              return n.unshift(e), t.push(n), t;
            };
          }),
          t.methods.forEach(function (e) {
            t[e] = t.factory(e);
          }),
          (t.load = function (t) {
            var e = 3e5,
              n = Math.ceil(new Date() / e) * e,
              o = document.createElement('script');
            (o.type = 'text/javascript'),
              (o.async = !0),
              (o.crossorigin = 'anonymous'),
              (o.src = 'https://js.driftt.com/include/' + n + '/' + t + '.js');
            var i = document.getElementsByTagName('script')[0];
            i.parentNode.insertBefore(o, i);
          });
      }
    })();

    // Configure drift
    window.drift.SNIPPET_VERSION = '0.3.1';
    window.drift.load('5namchumz6xk');

    // Optional: Configure drift to respect Do Not Track
    window.drift.config({
      doNotTrack: navigator.doNotTrack === '1',
    });

    setIsDriftLoaded(true);
  };

  useEffect(() => {
    // Check initial consent state
    const hasInitialConsent = checkCurrentConsentState();
    setHasConsentedToFunctional(hasInitialConsent);

    if (hasInitialConsent) {
      initializeDrift();
    }

    // Set up dataLayer monitoring for consent changes
    const originalPush = window.dataLayer?.push;
    if (originalPush && typeof window !== 'undefined') {
      window.dataLayer.push = function(...args) {
        // Call original push first
        const result = originalPush.apply(this, args);
        
        // Check if any of the pushed items is a consent update
        args.forEach(item => {
          if (item?.event === 'consent_updated' && item?.consent_state) {
            const functionalGranted = item.consent_state.functional === 'granted';
            setHasConsentedToFunctional(functionalGranted);
            
            if (functionalGranted && !isDriftLoaded) {
              initializeDrift();
            }
          }
        });
        
        return result;
      };

      // Cleanup function to restore original push
      return () => {
        if (window.dataLayer && originalPush) {
          window.dataLayer.push = originalPush;
        }
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDriftLoaded]);

  useEffect(() => {
    // Call drift.page() on each route change, but only if Drift is loaded and consent is granted
    if (window.drift && hasConsentedToFunctional && isDriftLoaded) {
      window.drift.page(pathname + searchParams.toString());
    }
  }, [pathname, searchParams, hasConsentedToFunctional, isDriftLoaded]);

  return null;
}
